import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DepartmentDto, SourcingEventStatus, useSetHomeDepartmentMutation } from "../../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../../common/auth/useLoggedInWithOrgContextState";
import { showsHomeDepartment } from "../../../../../common/departments/departmentVisibility";
import { departmentName, departmentsForNewEvent } from "../../../../../common/departments/eventCreationDepartments";
import { useApiError } from "../../../../../common/errors/useApiError";

interface Props {
  eventId: string;
  status: SourcingEventStatus;
  homeDepartment: DepartmentDto | null | undefined;
}

export const HomeDepartmentField = ({ eventId, status, homeDepartment }: Props) => {
  const { t } = useTranslation();
  const authState = useLoggedInWithOrgContextState();
  const toast = useToast();
  const { trigger } = useApiError();
  const [setHomeDepartment, { isLoading }] = useSetHomeDepartmentMutation();
  const [pendingMove, setPendingMove] = useState<string | null>(null);

  const departments = departmentsForNewEvent(authState.selectedOrg);
  const homeDepartmentId = homeDepartment?.id;
  const isDraft = status === SourcingEventStatus.Draft;

  const apply = async (departmentId: string) => {
    try {
      await setHomeDepartment({ eventId, setHomeDepartmentRequest: { homeDepartmentId: departmentId } }).unwrap();
      setPendingMove(null);
      toast({
        title: t("The procurement was moved to {{name}}", {
          name: departmentName(authState.selectedOrg, departmentId),
        }),
        status: "success",
        position: "top-right",
      });
    } catch (e) {
      trigger(e);
    }
  };

  // BFF: ManageSourcingEvent can arrive from an event share, which carries no right to create anywhere.
  const canMove = departments.some((d) => d.id === homeDepartmentId) || (!homeDepartmentId && departments.length > 0);

  if (!showsHomeDepartment(authState.selectedOrg, homeDepartmentId)) return null;

  if (!isDraft || !canMove) {
    return (
      <FormControl mb="2">
        <FormLabel mb="0">{t("Department")}</FormLabel>
        <Text fontWeight="semibold">{homeDepartment?.name ?? "–"}</Text>
        <FormHelperText>
          {!isDraft
            ? t("Locked when the procurement was published")
            : t("You cannot move the procurement to another department")}
        </FormHelperText>
      </FormControl>
    );
  }

  return (
    <>
      <FormControl isRequired mb="2">
        <FormLabel>{t("Department")}</FormLabel>
        <Select
          value={homeDepartmentId ?? ""}
          placeholder={homeDepartmentId ? undefined : t("Select department") ?? undefined}
          isDisabled={isLoading}
          onChange={(e) => {
            const next = e.target.value;
            if (!next || next === homeDepartmentId) return;
            if (homeDepartmentId) setPendingMove(next);
            else void apply(next);
          }}
        >
          {departments.map((department) => (
            <option key={department.id} value={department.id}>
              {department.name}
            </option>
          ))}
        </Select>
        <FormHelperText>
          {t("Decides who in the organization can see the procurement. Locked when it is published.")}
        </FormHelperText>
      </FormControl>

      <Modal isOpen={pendingMove !== null} onClose={() => setPendingMove(null)} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t("Move the procurement?")}</ModalHeader>
          <ModalBody>
            <Text>
              {t("The procurement moves from {{from}} to {{to}}, and that changes who can see it.", {
                from: homeDepartment?.name ?? "–",
                to: departmentName(authState.selectedOrg, pendingMove) ?? "–",
              })}
            </Text>
          </ModalBody>
          <ModalFooter gap="2">
            <Button variant="ghost" onClick={() => setPendingMove(null)} isDisabled={isLoading}>
              {t("Cancel")}
            </Button>
            <Button
              colorScheme="teal"
              isLoading={isLoading}
              onClick={() => pendingMove && void apply(pendingMove)}
            >
              {t("Move the procurement")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
